import { useMemo } from 'react'

import { getUrl } from 'app/UrlService'
import { useObjects } from 'data/hooks/objects'
import { useViews } from 'data/hooks/views'
import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'
import {
    LinkBlocksWidgetLink,
    LinkBlocksWidgetLinkTargetField,
    LinkBlocksWidgetLinkTargetUrl,
    LinkBlocksWidgetLinkTargetView,
} from 'features/views/LayoutEditor/widgets/LinkBlocksWidget/types'
import { getLinkBlockTargetType } from 'features/views/LayoutEditor/widgets/LinkBlocksWidget/utils'
import { useRecordManagerContext } from 'features/views/RecordManager/useRecordManagerContext'

export function useLinkBlocksWidgetLinkTargetState(link: LinkBlocksWidgetLink) {
    const { record } = useRecordManagerContext()
    const { fields } = useLayoutEditorContext()

    const { data: views = [] } = useViews()
    const { data: objects = [] } = useObjects()

    const { link: linkTarget, title, subtitle } = link
    const linkTargetType = getLinkBlockTargetType(link)

    let url: string | undefined = undefined
    let placeholderLabel: string | undefined = undefined
    switch (linkTargetType) {
        case 'url':
            url = (linkTarget as LinkBlocksWidgetLinkTargetUrl)?.href
            url = url ? encodeURI(url) : undefined
            placeholderLabel = getPlaceholderLabelUrl(url)
            break
        case 'field': {
            const fieldApiName = (linkTarget as LinkBlocksWidgetLinkTargetField)?.fieldApiName ?? ''
            const field = fields.find((f) => f.api_name === fieldApiName)
            if (!!field) {
                const urlValue = record?.[fieldApiName] ?? undefined
                url = extractUrlFromFieldValue(field, urlValue)
                placeholderLabel = getPlaceholderLabelUrl(url)
            }
            break
        }
        case 'view':
            const { viewSid = '', objectSid } = (linkTarget as LinkBlocksWidgetLinkTargetView) ?? {}
            const view = getViewUrl(viewSid, objectSid || null, views, objects)
            if (!!view) {
                url = view[0]
                placeholderLabel = view[1]
            }
            break
    }

    const label = linkTarget?.label || placeholderLabel
    const hasLabel = !!title || !!subtitle

    const shouldHide = !linkTarget || !url

    const openAs = linkTarget?.openAs
    const target = openAs === 'new_tab' ? '_blank' : undefined

    const isUrlRelative = url?.startsWith('/')

    return useMemo(
        () => ({
            label,
            hasLabel,
            url,
            shouldHide,
            target,
            isUrlRelative,
        }),
        [label, hasLabel, url, shouldHide, target, isUrlRelative]
    )
}

function extractUrlFromFieldValue(field: FieldDto, value: unknown) {
    // Extract the URL from the attachment value.
    if (field.type === 'multi_file') {
        if (Array.isArray(value)) {
            return value[0]?.url
        } else if (value && typeof value === 'object') {
            return (value as any).url
        }
    } else if (field.type === 'url') {
        return value as string
    }

    return undefined
}

function getViewUrl(
    viewSid: string,
    objectSid: string | null,
    views: ViewDto[],
    objects: ObjectDto[]
): [string, string] | undefined {
    const view = views.find((v) => v._sid === viewSid && v.object_id === objectSid)
    if (!view) return undefined

    const object = objects.find((o) => o._sid === objectSid)

    if (view.type === 'create') {
        if (!object) return undefined

        return [getUrl(`${object.url}/new`), `Add new ${object.name.toLowerCase()}`]
    }

    const url = getUrl(view.url)
    const label = view.name || object?.name || getPlaceholderLabelUrl(url)

    return [url, label]
}

const PLACEHOLDER_LABEL_URL_REGEX = /^https?:\/\//

function getPlaceholderLabelUrl(url?: string) {
    let newUrl = url

    const isRelativeUrl = url?.startsWith('/')
    if (isRelativeUrl) {
        const placeholderLabelUrl = new URL(window.location.href)
        placeholderLabelUrl.pathname = url ?? ''

        newUrl = placeholderLabelUrl.toString()
    }

    // Remove protocol.
    newUrl = newUrl?.replace(PLACEHOLDER_LABEL_URL_REGEX, '')

    return newUrl ?? ''
}
